import DashboardIcon from '@/Components/Common/Icons/DashboardIcon.svg';
import {
  AUTO_REFRESH_DELAY,
  CONFIG_BIOMETRICS_ENABLE_KEY,
  CONFIG_SMARTHOME_ENABLE_KEY,
  SESSION_STORAGE_KEYS,
} from '@/constants/device';
import { LoadingOutlined } from '@ant-design/icons';
import { onLogout } from '@vantiva-sa/homesight_humaui/Services/Common';
import { getCurrentUser } from '@vantiva-sa/homesight_humaui/Store/authStore';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { getProfilePics, getUserInfo } from '@vantiva-sa/homesight_humaui/Store/dataStore';
import { getNotificationMiniBannerMessage } from '@vantiva-sa/homesight_humaui/Store/notificationStore';
import { getName, getProfilePicUrl } from '@vantiva-sa/homesight_humaui/Utilities/CommonUtilities';
import {
  NavigationKeys,
  PhotoResolution,
  UserRoles,
  UserTypes,
} from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import SessionStorage from '@vantiva-sa/homesight_humaui/Utilities/SessionStorage';
import { get, isUndefined } from 'lodash';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  AccountIcon,
  BadgeIcon,
  ContactImage,
  ContactsIcon,
  ContentActivityIcon,
  DeviceInventoryIcon,
  DevicesNewIcon,
  GearIcon,
  GroupsIcon,
  HomeSightNewLogo,
  NewCallIcon,
  NewHelpIcon,
  NewLogoutIcon,
  NewScheduleIcon,
  ScheduleIconNew,
  ShieldIconCheck,
} from './Components/Common/Icons';
import { MotionActivityIcon } from './Components/Common/Icons/MotionActivityIcon';
import { SmartHomeIcon } from './Components/Common/Icons/SmartHomeIcon';
import { WellnessIcon } from './Components/Common/Icons/WellnessIcon';
import { GalleryIcon } from './Components/Common/Icons/GalleryIcon';
import useGetAdminDashboardByDateTime from './Components/Hooks/useGetAdminDashboardByDateTime';
import useSchedule from './Components/Hooks/useSchedule';
import useServiceProvider from './Components/Hooks/useServiceProvider';
import { hasDashboardAccess } from './Components/HubDetails/HubDetailsUtils';
import NotificationMiniBanner from './Components/Notifications/NotificationMiniBanner';
import { FEATURE_FLAG_CONFIG } from './constants/device';
import { useMainContext } from './MainContext';

const CHECK_INTERVAL_MS = 20000;
const SHOW_STATUS_BEFORE_MINUTES = 15;
const SHOW_JOIN_STATUS_AFTER_MINUTES = 15;
const ONGOING_MEETING_DISPLAY_TIME_HOURS = 4;
const SHOW_JOIN_STATUS_TILL_MINUTES = ONGOING_MEETING_DISPLAY_TIME_HOURS * 60;

const getDateTimeFromWebSocket = (wsEvent) => {
  if (wsEvent == null) return;
  let dateTime = null;
  if ('event_timestamp' in wsEvent) dateTime = moment(+wsEvent.event_timestamp);
  else {
    dateTime = moment(wsEvent.event_time, 'HH:mm');
    dateTime.add(dateTime.utcOffset(), 'minutes');
  }
  return dateTime;
};

const SVGImage = ({ imageUrl, className }) => {
  return (
    <img
      src={imageUrl}
      alt="PNG Image"
      className={className}
      style={{ maxWidth: '156px', maxHeight: '69px', aspectRatio: '2.2609' }}
    />
  );
};

export const MainSider = ({
  readOnlyHeader,
  openSubMenu,
  activeKey,
  onChange,
  hubConfig,
  hubUserRole,
  buttonSelected,
  onActionClick,
  navigationKey,
  offset,
  limit,
  renderProfileSection,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { handleMainMenuClick, navigationGuard } = useMainContext();
  const path = location.pathname.split('/')[1]?.toLowerCase();
  const currentUser = useSelector(getCurrentUser);
  const useruuid = useMemo(() => currentUser?.useruuid, [currentUser]);
  const trans = useSelector(translator);
  const [, scheduleList, fetchSchedule] = useSchedule();
  const userInfo = useSelector(getUserInfo(useruuid));
  const photoUrl = getProfilePicUrl(useruuid, useSelector(getProfilePics()), PhotoResolution.R256);
  const userData = useSelector((user) => user.data.userInfo);
  const updatedUserInfo = userData[useruuid];
  const firstname_updated = getName(updatedUserInfo);
  const [refetchDelay, setRefetchDelay] = useState(AUTO_REFRESH_DELAY);

  const [currentdatetime, setCurrentDateTime] = useState();
  const [startdatetime, setStartDateTime] = useState();

  const providerid = useSelector(getCurrentUser)?.providerid;
  const [providerData] = useServiceProvider({ providerid, forceFetch: false });

  const permission_level = useMemo(() => currentUser?.permission_level, [currentUser]);

  const { data: stats = {}, isLoading: isFetchingStats } = useGetAdminDashboardByDateTime({
    currentdatetime,
    startdatetime,
    refetchInterval: refetchDelay,
  });

  const isSmartHomeEnabled = useMemo(
    () => get(hubConfig, CONFIG_SMARTHOME_ENABLE_KEY, true),
    [hubConfig]
  );
  const isBiometricsEnabled = useMemo(
    () => get(hubConfig, CONFIG_BIOMETRICS_ENABLE_KEY, true),
    [hubConfig]
  );

  // Define a function to get translated permission level
  const getTranslatedPermissionLevel = (permission_level) => {
    switch (permission_level) {
      case 'Staff':
        return trans('STAFF');
      case 'Staff Admin':
        return trans('STAFFADMIN');
      case 'Account Principal':
        return trans('ACCOUNTPRINCIPAL');
      case 'Social':
        return trans('SOCIAL');
      case 'Care':
        return trans('CARE');
      case 'Manage':
        return trans('MANAGE');
      default:
        return permission_level;
    }
  };

  const [selectedMenu, setSelectedMenu] = useState(
    navigationKey ? NavigationKeys.accounts : path || NavigationKeys.accounts
  );
  const [status, setStatus] = useState(null);
  const createSearchParams = () => {
    const currentSearchParams = new URLSearchParams(location.search);
    const paramsMap = new Map(currentSearchParams.entries());
    const uniqueSearchParams = new URLSearchParams(paramsMap);
    return uniqueSearchParams.toString();
  };

  const renderHelpIcon = (
    <div className="action non-clickable-item" title={trans('HELP')}>
      <NewHelpIcon className="help-icon" style={{ height: '30px', width: '30px' }} />
    </div>
  );
  const renderLogoutIcon = (
    <div className="action non-clickable-item" title={trans('LOGOUT')}>
      <NewLogoutIcon className="logout-icon" style={{ height: '26px', width: '26px' }} />
    </div>
  );

  const renderGearIcon = (
    <div className="action non-clickable-item" title={trans('EDITPROFILE')}>
      <GearIcon className="gear-icon" style={{ height: '26px', width: '26px' }} />
    </div>
  );

  const handleSubMenuClick = (e, submenuKey) => {
    e.preventDefault();
    const proceed = () => onChange(submenuKey);
    if (navigationGuard) {
      navigationGuard(proceed);
    } else {
      proceed();
    }
  };

  const menuClassname = (key) =>
    `new-ui-menu-item ${selectedMenu === key ? 'new-ui-active-menu-item' : ''}`;

  useEffect(() => {
    const time = new Date(new Date().setHours(0, 0, 0, 0));
    fetchSchedule(useruuid, time);
    setCurrentDateTime(new Date().toISOString());
    setStartDateTime(time.toISOString());
  }, []);

  const [eventInfo, setEventInfo] = useState(null);
  const webSocketMessage = useSelector(getNotificationMiniBannerMessage);

  const menuItems = () => [
    {
      key: NavigationKeys.accounts,
      icon: <AccountIcon className="new-ui-icon" />,
      label: permission_level === 'Staff Admin' ? trans('ACCOUNTS') : trans('MYACCOUNTS'),
      count: isFetchingStats ? <LoadingOutlined /> : stats?.accounts?.total,
      submenu: [
        ...(hasDashboardAccess(hubUserRole)
          ? [
              {
                key: 'dashboard',
                label: trans('DASHBOARD'),
                icon: (
                  <DashboardIcon
                    className={`dashboard-icon ${activeKey === 'dashboard' ? 'active-icon' : 'inactive-icon'}`}
                    height={20}
                    width={20}
                  />
                ),
                access: [UserRoles.manage, UserRoles.care, UserRoles.social, UserRoles.empty],
              },
            ]
          : []),
        {
          key: 'schedule',
          label: trans('SCHEDULE'),
          icon: (
            <ScheduleIconNew
              className={`${
                activeKey === 'schedule' ? 'active-calendar-icon' : 'inactive-calendar-icon'
              }`}
            />
          ), // Label for the submenu item
          access: [UserRoles.manage, UserRoles.care, UserRoles.social, UserRoles.empty],
        },
        {
          key: 'contacts',
          label: trans('CONTACTS'),
          icon: (
            <ContactsIcon
              className={`${
                activeKey === 'contacts' ? 'active-contact-icon' : 'inactive-contact-icon'
              }`}
            />
          ),
          access: [UserRoles.manage, UserRoles.care, UserRoles.social, UserRoles.empty],
        },
        {
          key: 'callActivity',
          label: trans('CALLACTIVITY'),
          icon: (
            <NewCallIcon
              className={`${
                activeKey === 'callActivity' ? 'active-call-icon' : 'inactive-call-icon'
              }`}
            />
          ),
          access: [UserRoles.manage, UserRoles.care, UserRoles.social],
        },
        {
          key: 'contentActivity',
          label: trans('CONTENTACTIVITY'),
          icon: (
            <ContentActivityIcon
              className={`${activeKey === 'contentActivity' ? 'content-activity active-icon' : 'content-activity inactive-icon'}`}
            />
          ),
          access: [UserRoles.manage, UserRoles.care],
        },
        {
          key: 'motionactivity',
          label: trans('MOTIONACTIVITY'),
          icon: (
            <MotionActivityIcon
              className={`${activeKey === 'motionactivity' ? 'active-icon' : 'inactive-icon'}`}
            />
          ),
          access: [UserRoles.manage, UserRoles.care],
        },
        ...(isSmartHomeEnabled
          ? [
              {
                key: 'environmental',
                label: trans('SMART_HOME'),
                icon: (
                  <SmartHomeIcon
                    className={`${activeKey === 'environmental' ? 'active-icon' : 'inactive-icon'}`}
                  />
                ),
                access: [UserRoles.manage, UserRoles.care],
              },
            ]
          : []),
        ...(isBiometricsEnabled
          ? [
              {
                key: 'wellness',
                label: trans('BIOMETRICS'),
                icon: (
                  <WellnessIcon
                    className={`${activeKey === 'wellness' ? 'active-icon' : 'inactive-icon'}`}
                  />
                ),
                access: [UserRoles.manage, UserRoles.care],
              },
            ]
          : []),
        {
          key: 'gallery',
          label: trans('GALLERY'),
          icon: (
            <GalleryIcon
              className={`${activeKey === 'gallery' ? 'active-icon' : 'inactive-icon'}`}
            />
          ),
          access: [UserRoles.manage, UserRoles.care, UserRoles.social, UserRoles.empty],
        },
        {
          key: 'deviceinventory',
          label: trans('DEVICES'),
          icon: (
            <DeviceInventoryIcon
              className={`${activeKey === 'deviceinventory' ? 'active-icon' : 'inactive-icon'}`}
            />
          ),
          access:
            permission_level === 'Staff Admin'
              ? [UserRoles.manage, UserRoles.care, UserRoles.social, UserRoles.empty]
              : [UserRoles.manage, UserRoles.care],
        },
      ].filter((submenu) => {
        if (submenu.access && !submenu.access.includes(hubUserRole)) {
          return false; // Remove submenu if hubUserRole does not have required access
        }
        // Map submenu keys to their corresponding feature flag names
        const featureFlagKey = FEATURE_FLAG_CONFIG[submenu.key];
        if (featureFlagKey) {
          const featureEnabled = hubConfig?.homesight_feature_config?.[featureFlagKey];
          if (featureEnabled === false) {
            return false; // Hide submenu when its corresponding feature flag is disabled
          }
        }
        return true; // Keep the submenu if it satisfies access and feature flag conditions
      }),
    },
    {
      key: NavigationKeys.schedule,
      icon: <NewScheduleIcon className="new-ui-icon" />,
      label: trans('MYSCHEDULE'),
    },
    {
      key: NavigationKeys.groups,
      icon: <GroupsIcon className="new-ui-icon" />,
      label: trans('GROUPS'),
      count: isFetchingStats ? <LoadingOutlined /> : stats?.groups?.total,
      access: [UserTypes.providerAdmin, UserTypes.staff],
    },
    {
      key: NavigationKeys.staff,
      icon: <BadgeIcon className="new-ui-icon" />,
      label: trans('STAFF'),
      count: isFetchingStats ? <LoadingOutlined /> : stats?.staff?.total,
      access: [UserTypes.providerAdmin, UserTypes.staff],
    },
    {
      key: NavigationKeys.devices,
      icon: <DevicesNewIcon className="new-ui-icon" />,
      label: trans('INVENTORY'),
      count: isFetchingStats ? <LoadingOutlined /> : stats?.devices?.total,
      access: [UserTypes.providerAdmin, UserTypes.staff],
      // access_perm_level: [UserPermissionLevel.staffAdmin],
    },
  ];

  const checkForOnGoingEvent = (scheduleList, wsEvent) => {
    let now = moment().subtract(ONGOING_MEETING_DISPLAY_TIME_HOURS, 'hour');
    let schEvent = scheduleList.reverse().find((event) => {
      let time = moment(event.display_event_time, 'HH:mm:ss');
      if ('display_day' in event) {
        if (event.display_day !== now.format('dddd')) return;
      }
      if (time.isAfter(now)) return event;
    });
    let event = schEvent;

    if (wsEvent != null) {
      let dateTime = getDateTimeFromWebSocket(wsEvent);
      if (schEvent === undefined) {
        if (dateTime.isAfter(now)) event = wsEvent;
      } else {
        if (dateTime.isAfter(moment(schEvent.display_event_time, 'HH:mm:ss'))) event = wsEvent;
      }
    }
    return event;
  };

  useEffect(() => {
    if (isUndefined(navigationKey) && selectedMenu === NavigationKeys.accounts) {
      setRefetchDelay(AUTO_REFRESH_DELAY);
    } else {
      setRefetchDelay(false);
    }
  }, [navigationKey, selectedMenu]);

  useEffect(() => {
    // Find scheduled event after current time if any.
    // Meetings which are past SHOW_JOIN_STATUS_AFTER_MINUTES thier scheduled time are also to be considered.
    let now = moment().subtract(SHOW_JOIN_STATUS_AFTER_MINUTES, 'minute');
    let schEvent = scheduleList.find((event) => {
      let time = moment(event.display_event_time, 'HH:mm:ss');
      if ('display_day' in event) {
        if (event.display_day !== now.format('dddd')) return;
      }
      if (time.isAfter(now)) return event;
    });
    const wsEvent = webSocketMessage?.schedule_event;
    let event = schEvent;

    // Check for web socket message cases compared to scheduled event
    if (wsEvent != null) {
      if (wsEvent.action?.includes('delete')) {
        setEventInfo(null);
        fetchSchedule(useruuid, new Date(new Date().setHours(0, 0, 0, 0)));
        return;
      }
      let dateTime = getDateTimeFromWebSocket(wsEvent);
      if (
        dateTime != null &&
        dateTime.isAfter(now) &&
        (schEvent === undefined ||
          moment(schEvent.display_event_time, 'HH:mm:ss').isAfter(dateTime))
      )
        event = wsEvent;
    }

    if (event == null) {
      event = checkForOnGoingEvent(scheduleList, webSocketMessage);
      if (event == null) return;
    }

    const eventInfo = {
      eventInfo: event,
      eventId: event.schedule_eventuuid,
      time: '',
      label: event.event_name,
    };
    if ('display_event_time' in event) {
      let dateTime = moment(event.display_event_time, 'HH:mm:ss');
      eventInfo.time = dateTime.format('h:mm A');
    } else if ('event_timestamp' in event) {
      let dateTime = moment(+event.event_timestamp);
      eventInfo.time = dateTime.format('h:mm A');
    } else {
      let time = moment(event.event_time, 'HH:mm');
      time.add(time.utcOffset(), 'minutes');
      eventInfo.time = time.format('h:mm A');
    }
    setEventInfo(eventInfo);
  }, [webSocketMessage, scheduleList]);

  useEffect(() => {
    let interval;
    if (eventInfo !== null) {
      updateStatus();
      interval = setInterval(() => {
        updateStatus();
      }, CHECK_INTERVAL_MS);
    }

    return () => {
      if (interval !== undefined) clearInterval(interval);
    };
  }, [eventInfo]);

  const updateStatus = () => {
    const now = moment();
    const eventTime = moment(eventInfo.time, 'h:mm A');
    const diff = eventTime.diff(now, 'minute');
    if (diff <= SHOW_STATUS_BEFORE_MINUTES && diff >= 1) setStatus(trans('UPCOMING'));
    // else if (diff < 1 && diff >= -SHOW_JOIN_STATUS_TILL_MINUTES) {
    //   eventInfo?.eventInfo?.event_type === sessionType.zoom
    //     ? setStatus("Zoom Call")
    //     : setStatus("Join");
    // } else if (diff < -SHOW_JOIN_STATUS_TILL_MINUTES) setEventInfo(null);
    else setStatus(null);
  };

  useEffect(() => {
    const nextDate = moment().set({
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0,
      date: moment().date() + 1,
    });
    setTimeout(
      () => {
        fetchSchedule(useruuid, new Date(new Date().setHours(0, 0, 0, 0)));
      },
      nextDate.diff(moment()) + 2000
    );
  }, []);

  return (
    <div className="new-ui-mainsider-wrapper">
      <div className="new-ui-top-container">
        <div className="new-ui-main-logo-div">
          {!providerData ? (
            // Render an empty div if data is not fetched
            <div className="empty-logo-div"></div>
          ) : providerData.light_logo_url ? (
            // Data is fetched, and light_logo_url is available
            <div className="main-logo-div">
              <SVGImage imageUrl={providerData.light_logo_url} className="custom-svg-class" />
            </div>
          ) : (
            // Data is fetched, but light_logo_url is not available
            <div className="main-logo-div">
              <HomeSightNewLogo />
            </div>
          )}
        </div>
        <div className="new-ui-provider-name-container">
          <span className="new-ui-provider-name-text">
            {/* showing provider name from ServiceProviderAPI, fallback to token value in case of API error */}
            {providerData ? providerData.displayname : ''}
          </span>
        </div>

        {menuItems().map((menu) => {
          if (
            (menu.access && !menu.access.includes(userInfo.usertype)) ||
            (menu.access_perm_level && !menu.access_perm_level.includes(userInfo.permission_level))
          ) {
            return null;
          }
          return (
            <Link
              to={{
                pathname: '/' + menu.key,
                search: '?' + createSearchParams(),
              }}
              key={menu.key}
              className="new-ui-menu-container"
              state={{ count: menu.count }}
            >
              <div className={menuClassname(menu.key)}>
                <div
                  className="new-ui-menu-item-header"
                  onClick={(e) => {
                    e.preventDefault();
                    const proceed = () => {
                      if ((menu.key === NavigationKeys.accounts || navigationKey) && openSubMenu) {
                        if (navigationKey) {
                          onActionClick(null, null, offset, limit, true)();
                        } else {
                          onActionClick(null, null, false, true)();
                        }
                      } else {
                        navigate('/' + menu.key + '?' + createSearchParams());
                      }
                      handleMainMenuClick(menu.key);
                    };
                    if (navigationGuard) {
                      navigationGuard(proceed);
                    } else {
                      proceed();
                    }
                  }}
                >
                  <div className="new-ui-menu-icon-label-container">
                    <div className="new-ui-menu-icon">{menu.icon}</div>
                    <div className="new-ui-menu-label">{menu.label}</div>
                  </div>
                  <div className="new-ui-menu-count-container">
                    <div className="new-ui-menu-count">{menu.count}</div>
                  </div>
                  {menu.key === 'schedule' && (
                    <div className="new-ui-menu-item-body">
                      <div className="new-ui-body-row-1">
                        {status && <div className="new-ui-status">{status}</div>}
                      </div>
                    </div>
                  )}
                </div>

                {openSubMenu && menu.submenu && (
                  <>
                    <div className="submenu-container">
                      {renderProfileSection && renderProfileSection()}
                      <div className="submenu-container-items">
                        {menu.submenu.map((submenuItem) => (
                          <div
                            key={submenuItem.key}
                            className={`submenu-item ${
                              activeKey === submenuItem.key ? 'active' : 'inactive'
                            }`}
                            onClick={(e) => handleSubMenuClick(e, submenuItem.key)}
                          >
                            <div className="submenu-icon-label-container">
                              <div className={`sub-menu-icon`}>{submenuItem.icon}</div>
                              <div
                                className={`sub-menu-label ${
                                  activeKey === submenuItem.key ? 'active-label' : 'inactive-label'
                                }`}
                              >
                                {submenuItem.label}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </Link>
          );
        })}
      </div>
      <div className="new-ui-bottom-container">
        <div className="new-ui-userinfo-container">
          <div className="new-ui-image-container">
            {photoUrl == null ? (
              <ContactImage className="new-ui-user-profile-pic svg" />
            ) : (
              <img className="new-ui-user-profile-pic img" alt="" src={photoUrl} />
            )}
            <div className="new-ui-info-container">
              <span className="new-ui-name" title={firstname_updated}>
                {firstname_updated.length > 12
                  ? firstname_updated.substring(0, 12) + '...'
                  : firstname_updated}
              </span>
              <div className="new-ui-permission-level">
                {permission_level && (
                  <span>
                    <div className="main-sider-permission-level-container">
                      <span
                        className={
                          permission_level === 'Staff'
                            ? 'new-ui-staffColor'
                            : permission_level === 'Staff Admin'
                              ? 'new-ui-staffAdminColor'
                              : 'new-ui-accountPrincipalSocialRole'
                        }
                      >
                        {(permission_level === 'Staff Admin' || permission_level === 'Staff') && (
                          <ShieldIconCheck
                            className={`shield-icon ${
                              permission_level === 'Staff' ? 'staff-shield' : 'staff-admin-shield'
                            }`}
                          />
                        )}
                        <span
                          className={
                            permission_level === 'Staff'
                              ? 'new-ui-staff-label'
                              : permission_level === 'Staff Admin'
                                ? 'new-ui-staffAdminColor-label'
                                : 'new-ui-accountPrincipalSocialRole-label'
                          }
                        >
                          {getTranslatedPermissionLevel(permission_level)}
                        </span>
                      </span>
                    </div>
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="new-ui-logout-gear-notification-help-icons">
          <div
            className={`new-ui-notification-container ${
              buttonSelected === 'notification' ? 'active-icon' : ''
            }`}
          >
            <NotificationMiniBanner
              isSelected={buttonSelected === 'notification'}
              readOnly={readOnlyHeader}
              searchParams={createSearchParams}
            />
          </div>
          <div
            className={`new-ui-gear-container ${
              buttonSelected === 'gear' ? 'active-icon' : 'inactive-icon'
            }`}
          >
            {readOnlyHeader ? (
              renderGearIcon
            ) : (
              <Link
                to={{
                  pathname: '/' + NavigationKeys.profile,
                  search: '?' + createSearchParams(),
                }}
                onClick={(e) => {
                  if (!navigationGuard) return;
                  e.preventDefault();
                  navigationGuard(() =>
                    navigate('/' + NavigationKeys.profile + '?' + createSearchParams())
                  );
                }}
              >
                <div className="action" title={trans('EDITPROFILE')}>
                  <GearIcon style={{ height: '30px', width: '30px' }} />
                </div>
              </Link>
            )}
          </div>
          <div
            className={`new-ui-help-container ${
              buttonSelected === 'help' ? 'active-icon' : 'inactive-icon'
            }`}
          >
            {readOnlyHeader ? (
              renderHelpIcon
            ) : (
              <Link
                to={{
                  pathname: '/' + NavigationKeys.about,
                  search: '?' + createSearchParams(),
                }}
                onClick={(e) => {
                  if (!navigationGuard) return;
                  e.preventDefault();
                  navigationGuard(() =>
                    navigate('/' + NavigationKeys.about + '?' + createSearchParams())
                  );
                }}
              >
                <div className="action" title={trans('HELP')}>
                  <NewHelpIcon style={{ height: '30px', width: '30px' }} />
                </div>
              </Link>
            )}
          </div>
          <div className="new-ui-logout-container">
            {readOnlyHeader ? (
              renderLogoutIcon
            ) : (
              <div
                title={trans('LOGOUT')}
                className="action"
                onClick={
                  readOnlyHeader
                    ? null
                    : () => {
                        const proceed = () => {
                          SessionStorage.remove(SESSION_STORAGE_KEYS.FILTER);
                          SessionStorage.remove(SESSION_STORAGE_KEYS.INVENTORY_FILTER);
                          SessionStorage.remove(SESSION_STORAGE_KEYS.STAFF_FILTER);
                          onLogout();
                        };
                        if (navigationGuard) {
                          navigationGuard(proceed);
                        } else {
                          proceed();
                        }
                      }
                }
              >
                <NewLogoutIcon className="logout-icon" style={{ height: '26px', width: '26px' }} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

MainSider.propTypes = {};

export default MainSider;
